/**
 * EVE Experience Viewer
 *
 * Browse EVE's experience data with domain filtering, time range, search,
 * and expandable step detail cards.
 */

import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

interface StepDetail {
  step?: number;
  task?: string;
  url_before?: string;
  url_after?: string;
  url_changed?: boolean;
  element_id?: number;
  element_tag?: string;
  confidence?: number;
  source?: string;
  reasoning?: string;
  success?: boolean;
  error?: string;
  added_count?: number;
  removed_count?: number;
}

interface Experience {
  id: string;
  context_text: string;
  element_desc: string;
  action_type: string;
  action_value: string;
  url_pattern: string;
  outcome: string;
  timestamp: number;
  score: number;
  step_detail: StepDetail;
}

interface DomainStats {
  domain: string;
  count: number;
}

type TimeFilter = 'hour' | 'today' | 'week' | 'all';

const EVE_API = '/api/eve';

export const EveViewerPage: React.FC = () => {
  const [domains, setDomains] = useState<DomainStats[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const fetchDomains = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const res = await fetch(`${EVE_API}/domains`);
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setDomains(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Connection failed';
      setError(msg);
      toast.error('EVE API: ' + msg);
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  const fetchExperiences = useCallback(async () => {
    if (!selectedDomain) { setExperiences([]); return; }
    setIsLoading(true);
    try {
      const res = await fetch(`${EVE_API}/experiences?domain=${selectedDomain}&limit=500`);
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setExperiences(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDomain]);

  const filtered = experiences.filter((exp) => {
    if (timeFilter !== 'all') {
      const now = Date.now() / 1000;
      const cutoff = { hour: now - 3600, today: now - 86400, week: now - 604800 }[timeFilter];
      if (cutoff && exp.timestamp < cutoff) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return [exp.context_text, exp.element_desc, exp.action_value, exp.url_pattern]
        .some(f => f.toLowerCase().includes(q));
    }
    return true;
  });

  useEffect(() => { fetchDomains(); }, [fetchDomains]);
  useEffect(() => { fetchExperiences(); }, [selectedDomain, fetchExperiences]);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { fetchDomains(); fetchExperiences(); }, 10000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchDomains, fetchExperiences]);

  const fmtTime = (ts: number) => {
    const d = new Date(ts * 1000);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const fmtFull = (ts: number) => new Date(ts * 1000).toLocaleString();

  const actionColor: Record<string, string> = {
    click: 'bg-blue-100 text-blue-700',
    type: 'bg-green-100 text-green-700',
    select: 'bg-purple-100 text-purple-700',
  };

  const selectDomain = (d: string) => {
    setSelectedDomain(d);
    setExpandedId(null);
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  return (
    <div className="h-[calc(100vh-64px)] flex overflow-hidden bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-20 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed md:relative md:translate-x-0 z-30 w-64 h-full bg-white border-r border-gray-200 flex flex-col transition-transform duration-200`}>
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">EVE</h2>
            <p className="text-xs text-gray-500">Experience Store</p>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden p-1 text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-4 py-3 border-b border-gray-200 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Time Range</label>
            <select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="hour">Last Hour</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="all">All Time</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-700">Auto-refresh</label>
            <button onClick={() => setAutoRefresh(!autoRefresh)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${autoRefresh ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${autoRefresh ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <h3 className="text-xs font-medium text-gray-700 mb-2">Domains</h3>
          {domainsLoading ? (
            <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" /></div>
          ) : domains.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-4">{error ? 'API unavailable' : 'No domains'}</p>
          ) : (
            <div className="space-y-1">
              {domains.map((d) => (
                <button key={d.domain} onClick={() => selectDomain(d.domain)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                    selectedDomain === d.domain ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}>
                  <span className="truncate">{d.domain.replace(/_/g, '.')}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                    selectedDomain === d.domain ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
                  }`}>{d.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 ${sidebarOpen ? 'hidden md:block' : ''}`}>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {sidebarOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
          <div className="flex-1 relative">
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {selectedDomain && (
            <span className="hidden sm:inline text-xs text-gray-500 flex-shrink-0">{filtered.length} entries</span>
          )}
        </div>

        {/* Cards */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : !selectedDomain ? (
              <div className="text-center py-16 text-gray-400">
                <svg className="mx-auto h-12 w-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
                <p className="text-sm font-medium">Select a domain</p>
                <p className="text-xs mt-1">Choose a domain from the sidebar to browse experiences</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-sm">No experiences match your filters</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map((exp) => {
                  const isExpanded = expandedId === exp.id;
                  const d = exp.step_detail || {};
                  return (
                    <div key={exp.id}
                      className={`bg-white rounded-lg border transition-all ${isExpanded ? 'border-blue-200 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}>
                      {/* Summary row - always visible */}
                      <button onClick={() => setExpandedId(isExpanded ? null : exp.id)}
                        className="w-full text-left px-4 py-3 flex items-center gap-3">
                        <svg className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${actionColor[exp.action_type] || 'bg-gray-100 text-gray-700'}`}>
                          {exp.action_type}
                        </span>
                        <span className="text-sm text-gray-900 truncate flex-1">
                          {exp.element_desc}
                        </span>
                        {exp.action_value && (
                          <code className="text-xs text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded truncate max-w-[150px] flex-shrink-0">
                            {exp.action_value}
                          </code>
                        )}
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                          exp.outcome === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>{exp.outcome}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0 w-16 text-right">{fmtTime(exp.timestamp)}</span>
                      </button>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-3 text-xs">
                            <DetailRow label="Context" value={exp.context_text} full />
                            <DetailRow label="Element" value={exp.element_desc} />
                            <DetailRow label="Action" value={`${exp.action_type}${exp.action_value ? ` "${exp.action_value}"` : ''}`} />
                            <DetailRow label="URL" value={exp.url_pattern} />
                            <DetailRow label="Outcome" value={exp.outcome} />
                            <DetailRow label="Time" value={fmtFull(exp.timestamp)} />
                            {d.step != null && <DetailRow label="Step #" value={String(d.step)} />}
                            {d.source && <DetailRow label="Decision" value={`${d.source} (conf: ${(d.confidence ?? 0).toFixed(2)})`} />}
                            {d.reasoning && <DetailRow label="Reasoning" value={d.reasoning} full />}
                            {d.url_changed && <DetailRow label="URL Changed" value={`${d.url_before} → ${d.url_after}`} full />}
                            {d.element_id != null && <DetailRow label="Element ID" value={`#${d.element_id} <${d.element_tag || '?'}>`} />}
                            {(d.added_count != null && d.added_count > 0) && <DetailRow label="Elements Added" value={String(d.added_count)} />}
                            {(d.removed_count != null && d.removed_count > 0) && <DetailRow label="Elements Removed" value={String(d.removed_count)} />}
                            {d.error && <DetailRow label="Error" value={d.error} full />}
                            {d.task && <DetailRow label="Task" value={d.task} full />}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string; full?: boolean }> = ({ label, value, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <span className="text-gray-500 font-medium">{label}:</span>{' '}
    <span className="text-gray-800 break-words">{value}</span>
  </div>
);
